<script setup lang="ts">
  import type { MessageResponse } from '~/models/chat';
  import { useWindowSize } from '@vueuse/core';
  import { ChatMaxHeight } from '@/utils/content-utils';

  const props = defineProps({
    messages: {
      type: Array as PropType<MessageResponse[]>,
      default: () => []
    },
    canWrite: {
      type: Boolean,
      required: true
    }
  });

  const emit = defineEmits(['send', 'like', 'dislike', 'regenerate', 'acceptWebsite', 'declineWebsite']);

  const scrollRef = ref<HTMLElement | null>(null);
  const messagesRef = ref<HTMLElement | null>(null);
  const { height: windowHeight } = useWindowSize();
  const isFirstScroll = ref(true);
  const lastScrollPos = ref(0);
  const isAutoScroll = ref(true);

  const send = async (message: string) => {
    emit('send', message);
  };

  const like = async (messageId: string) => {
    emit('like', messageId);
  };

  const dislike = async (messageId: string) => {
    emit('dislike', messageId);
  };

  const regenerate = async (messageId: string) => {
    emit('regenerate', messageId);
  };

  const acceptWebsite = () => {
    emit('acceptWebsite');
  };

  const declineWebsite = () => {
    emit('declineWebsite');
  };

  const onRetry = (message: MessageResponse) => {
    if (!message.chameleonErrorRetryFn) return;
    message.chameleonErrorRetryFn();
  };

  const handleUserScroll = (event: Event) => {
    const element = event.target as HTMLElement;
    const scrollPos = element.scrollTop;

    isAutoScroll.value = scrollPos >= lastScrollPos.value;
    lastScrollPos.value = scrollPos;
  };

  onMounted(() => {
    const layout = document.querySelector('.chat-layout');
    layout?.addEventListener('scroll', handleUserScroll);
    scrollRef.value?.addEventListener('scroll', handleUserScroll);
  });

  onUnmounted(() => {
    const layout = document.querySelector('.chat-layout');
    layout?.removeEventListener('scroll', handleUserScroll);
    scrollRef.value?.removeEventListener('scroll', handleUserScroll);
  });

  watch(
    () => props.messages,
    (messages) => {
      const layout = document.querySelector('.chat-layout');
      const chatScreen = scrollRef.value;
      if (!messages.length || !messagesRef.value || !layout || !chatScreen) return;

      const scrollContainer = (windowHeight.value <= ChatMaxHeight ? layout : chatScreen) as HTMLElement;
      isFirstScroll.value = false;

      const lastMessage = messages[messages.length - 1];

      if (isAutoScroll.value && (lastMessage.event === 'MESSAGE_UPDATE' || lastMessage.author.username !== 'Ida')) {
        setTimeout(() => {
          if (!messagesRef.value) return;

          const messageElements = messagesRef.value.querySelectorAll('.chat-message') as any as HTMLElement[];
          const lastMsgElement = messageElements[messageElements.length - 1];
          const top = lastMsgElement.offsetTop;
          const scrollTop = Math.max(0, top - 80);

          scrollContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });
        }, 100);
      }
    },
    { immediate: true, deep: true }
  );

  const hasScrollbar = () => {
    if (!scrollRef.value) return false;

    const scrollTop = scrollRef.value.scrollTop;
    if (scrollTop > 0) return true;

    scrollRef.value.scrollTop += 10;
    if (scrollTop === scrollRef.value.scrollTop) return false;

    scrollRef.value.scrollTop = scrollTop;
    return true;
  };
</script>

<template>
  <div
    ref="scrollRef"
    :class="[
      'chat-screen',
      {
        'chat-screen--has-scrollbar': hasScrollbar()
      },
      'chat-chat-screen'
    ]">
    <div class="chat-screen__wrapper">
      <div
        ref="messagesRef"
        class="chat-chat-screen__messages">
        <template
          v-for="message in messages"
          :key="message.id">
          <ChatMessage
            v-if="!message.chameleonError"
            :message="message"
            @like="like"
            @dislike="dislike"
            @regenerate="regenerate"
            @accept-website="acceptWebsite"
            @decline-website="declineWebsite" />
          <ChatError
            v-else
            ref="messageRefs"
            :message="message.content"
            :time="message.timestamp"
            @retry="onRetry(message)" />
        </template>
      </div>
      <div class="chat-chat-screen__input">
        <ChatInput
          :can-write="canWrite"
          @send="send" />
      </div>
    </div>
  </div>
</template>
